import { z } from "zod";
import type { CreateVisitRequestBody } from "@/common/types/visits";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

export type VisitDraftBody = Omit<CreateVisitRequestBody, "name">;
const identity = {
  version: z.literal(2),
  key: z.string().uuid(),
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
};
const submissionSchema = z.discriminatedUnion("kind", [
  z.object({ ...identity, kind: z.literal("pending") }).strict(),
  z
    .object({
      ...identity,
      kind: z.literal("accepted"),
      visitPublicId: z.uuidv4().toLowerCase(),
      audioQueued: z.boolean(),
    })
    .strict(),
]);
export type VisitSubmission = z.infer<typeof submissionSchema>;
const prefix = "rosc:visit-submission:";
const storageKey = (account: string, draft: string): string =>
  `${prefix}${encodeURIComponent(account)}:${encodeURIComponent(draft)}`;

export const readVisitSubmission = (
  account: string,
  draft: string,
): VisitSubmission | null => {
  assertNoObsoleteSubmission(account, "rosc:visit-submission:");
  const value = sessionStorage.getItem(storageKey(account, draft));
  if (value === null) return null;
  try {
    return submissionSchema.parse(JSON.parse(value));
  } catch {
    throw new Error(
      "Saved submission cannot be read. Reselect the target in Visits before retrying. Your local draft is retained.",
    );
  }
};
const save = (account: string, draft: string, value: VisitSubmission): void => {
  sessionStorage.setItem(storageKey(account, draft), JSON.stringify(value));
};
export const prepareVisitSubmission = async (
  account: string,
  draft: string,
  body: VisitDraftBody,
): Promise<VisitSubmission> => {
  const encoded = new TextEncoder().encode(
    JSON.stringify([
      Math.ceil(body.duration),
      body.style.trim(),
      body.format.trim(),
      body.exam.trim(),
      body.template.trim(),
      body.language.trim().toLowerCase(),
      body.transcription,
      body.customTemplatePublicId ?? null,
      ...(body.title?.trim() ? [{ title: body.title.trim() }] : []),
      // Appended only when sent, so saved retries keep their fingerprint.
      ...(body.instructions !== undefined
        ? [{ instructions: body.instructions }]
        : []),
      ...(body.skipShortcuts?.length
        ? [{ skipShortcuts: body.skipShortcuts }]
        : []),
      ...(body.recordingConsent ? [{ recordingConsent: true }] : []),
    ]),
  );
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  const fingerprint = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  const existing = readVisitSubmission(account, draft);
  if (existing) {
    if (existing.fingerprint !== fingerprint)
      throw new Error(
        "This submission has changed. Restore its original input, or check Visits before abandoning it.",
      );
    return existing;
  }
  const pending = {
    version: 2,
    kind: "pending",
    key: crypto.randomUUID(),
    fingerprint,
  } satisfies VisitSubmission;
  // Persist identity before transport; never persist transcript or audio here.
  save(account, draft, pending);
  return pending;
};
export const completeVisitSubmission = (
  account: string,
  draft: string,
  submission: VisitSubmission,
  visitPublicId: string,
): VisitSubmission => {
  const accepted = {
    ...submission,
    version: 2,
    kind: "accepted",
    visitPublicId,
    audioQueued: false,
  } satisfies VisitSubmission;
  save(account, draft, accepted);
  return accepted;
};
export const markVisitAudioQueued = (
  account: string,
  draft: string,
  submission: VisitSubmission,
): void => {
  if (submission.kind !== "accepted")
    throw new Error("Visit must be accepted before attaching audio");
  save(account, draft, { ...submission, audioQueued: true });
};
export const clearVisitSubmission = (account: string, draft: string): void => {
  sessionStorage.removeItem(storageKey(account, draft));
};
const accountKeys = (account: string): string[] =>
  Object.keys(sessionStorage).filter((key) =>
    key.startsWith(`${prefix}${encodeURIComponent(account)}:`),
  );
export const hasPendingVisitSubmission = (account: string): boolean => {
  assertNoObsoleteSubmission(account, "rosc:visit-submission:");
  return accountKeys(account).some((key) => {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? "null");
    return submissionSchema.parse(value).kind === "pending";
  });
};
export const clearPendingVisitSubmissions = (account: string): void => {
  discardObsoleteSubmissions(account, "rosc:visit-submission:");
  for (const key of accountKeys(account)) {
    let accepted = false;
    try {
      accepted =
        submissionSchema.parse(
          JSON.parse(sessionStorage.getItem(key) ?? "null"),
        ).kind === "accepted";
    } catch {
      /* Explicit abandonment can also remove unreadable identity metadata. */
    }
    if (!accepted) sessionStorage.removeItem(key);
  }
};
export const beginVisitDraft = (account: string, draft: string): void => {
  if (hasPendingVisitSubmission(account))
    throw new Error(
      "Check Visits before abandoning the previous submission and starting another recording.",
    );
  clearVisitSubmission(account, draft);
};
export const clearVisitSubmissions = (): void => {
  for (const key of Object.keys(sessionStorage))
    if (key.startsWith(prefix)) sessionStorage.removeItem(key);
};
